import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Eye, Check, AlertCircle, Lock, ArrowLeftRight, Download } from "lucide-react";
import { formatCurrency } from "./utils";

export function ViewLancamentoDialog({ item, onClose, contas, centrosCusto }) {
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dialog-view-lancamento">
        <DialogHeader>
          <DialogTitle className="text-[#0A1628] flex items-center gap-2">
            <Eye className="h-5 w-5" />Detalhes do Lançamento
          </DialogTitle>
        </DialogHeader>
        {item && (
          <div className="space-y-3 py-2 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500">Tipo</Label><p className="font-medium">{item.tipo === "receita" ? "Receita" : "Despesa"}</p></div>
              <div><Label className="text-xs text-slate-500">Status</Label><Badge>{item.status}</Badge></div>
              <div className="col-span-2"><Label className="text-xs text-slate-500">Descrição</Label><p className="font-medium">{item.descricao || "-"}</p></div>
              <div><Label className="text-xs text-slate-500">Valor</Label><p className="font-mono font-semibold text-emerald-600">{formatCurrency(item.valor)}</p></div>
              <div><Label className="text-xs text-slate-500">Valor Pago</Label><p className="font-mono">{formatCurrency(item.valor_pago)}</p></div>
              <div><Label className="text-xs text-slate-500">Juros</Label><p className="font-mono text-red-600">{item.juros ? formatCurrency(item.juros) : "-"}</p></div>
              <div><Label className="text-xs text-slate-500">Multa</Label><p className="font-mono text-red-600">{item.multa ? formatCurrency(item.multa) : "-"}</p></div>
              <div><Label className="text-xs text-slate-500">Desconto</Label><p className="font-mono text-emerald-700">{item.desconto ? formatCurrency(item.desconto) : "-"}</p></div>
              <div><Label className="text-xs text-slate-500">Vencimento</Label><p>{item.data_vencimento}</p></div>
              <div><Label className="text-xs text-slate-500">Data Pagamento</Label><p>{item.data_pagamento || "-"}</p></div>
              <div><Label className="text-xs text-slate-500">Parcela</Label><p>{item.parcela || "-"}</p></div>
              <div><Label className="text-xs text-slate-500">Documento</Label><p>{item.documento || "-"}</p></div>
              <div><Label className="text-xs text-slate-500">Conta</Label><p>{contas.find(c => c.id === item.conta_id)?.nome || "-"}</p></div>
              <div><Label className="text-xs text-slate-500">Centro de Custo</Label><p>{centrosCusto.find(c => c.id === item.centro_custo_id)?.nome || "-"}</p></div>
            </div>
            {(item.contrato_id || item.orcamento_id) && (
              <div className="bg-blue-50 p-3 rounded text-xs">
                <p className="font-medium text-blue-900">Vinculado a:</p>
                {item.contrato_id && <p className="text-blue-700">Contrato ID: {item.contrato_id}</p>}
                {item.orcamento_id && <p className="text-blue-700">Orçamento ID: {item.orcamento_id}</p>}
              </div>
            )}
            {item.observacoes && (
              <div><Label className="text-xs text-slate-500">Observações</Label><p className="bg-slate-50 p-2 rounded">{item.observacoes}</p></div>
            )}
            <p className="text-[10px] text-slate-400">Criado em {item.created_at ? new Date(item.created_at).toLocaleString("pt-BR") : "-"} · Atualizado em {item.updated_at ? new Date(item.updated_at).toLocaleString("pt-BR") : "-"}</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteLancamentoDialog({ open, onOpenChange, item, password, setPassword, deleting, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onOpenChange(false); }}>
      <DialogContent className="max-w-md" data-testid="dialog-delete-lancamento">
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />Confirmar Exclusão
          </DialogTitle>
          <DialogDescription className="text-slate-700 pt-2">
            <strong>Deseja realmente excluir este lançamento? Esta ação não poderá ser desfeita.</strong>
          </DialogDescription>
        </DialogHeader>
        {item && (
          <div className="space-y-3 py-2">
            <div className="p-3 bg-red-50 rounded text-sm space-y-1">
              <p><b>{item.descricao}</b></p>
              <p className="text-slate-600">
                {item.tipo === "receita" ? "Receita" : "Despesa"} · {formatCurrency(item.valor)} · Venc: {item.data_vencimento}
              </p>
            </div>
            {(item.orcamento_id || item.contrato_id) && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex gap-2" data-testid="alerta-vinculo">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold mb-0.5">Atenção: lançamento vinculado</p>
                  <p>Este lançamento foi gerado automaticamente a partir de {item.contrato_id ? "um contrato" : "uma proposta"}. A exclusão remove apenas este lançamento — o {item.contrato_id ? "contrato" : "orçamento"} original permanece intacto.</p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><Lock className="h-3 w-3" />Digite sua senha para confirmar</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Senha do usuário logado"
                data-testid="input-delete-password"
                onKeyDown={(e) => { if (e.key === "Enter" && password) onConfirm(); }}
                autoFocus
              />
              <p className="text-[10px] text-slate-500">A exclusão é registrada no log de auditoria com seu usuário, IP e horário.</p>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleting}>Cancelar</Button>
          <Button
            className="bg-red-600 hover:bg-red-700"
            onClick={onConfirm}
            disabled={!password || deleting}
            data-testid="btn-confirm-delete"
          >
            {deleting ? "Excluindo..." : "Excluir definitivamente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BulkDeleteDialog({ open, onOpenChange, count, password, setPassword, deleting, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onOpenChange(false); }}>
      <DialogContent className="max-w-md" data-testid="dialog-bulk-delete">
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />Excluir {count} {count === 1 ? "lançamento" : "lançamentos"}
          </DialogTitle>
          <DialogDescription className="text-slate-700 pt-2">
            <strong>Esta ação não poderá ser desfeita.</strong> Lançamentos já pagos/baixados serão ignorados.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-2">
            <Label className="flex items-center gap-1"><Lock className="h-3 w-3" />Digite sua senha</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Senha do usuário logado"
              data-testid="input-bulk-password"
              onKeyDown={(e) => { if (e.key === "Enter" && password) onConfirm(); }}
              autoFocus
            />
            <p className="text-[10px] text-slate-500">Cada item excluído é registrado no log de auditoria.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleting}>Cancelar</Button>
          <Button
            className="bg-red-600 hover:bg-red-700"
            onClick={onConfirm}
            disabled={!password || deleting}
            data-testid="btn-confirm-bulk-delete"
          >
            {deleting ? "Excluindo..." : `Excluir ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BaixaDialog({ open, onOpenChange, item, baixaData, setBaixaData, bancos, isReceber, onConfirm }) {
  const valorOrig = parseFloat(item?.valor) || 0;
  const juros = parseFloat(baixaData.juros) || 0;
  const multa = parseFloat(baixaData.multa) || 0;
  const desc = parseFloat(baixaData.desconto) || 0;
  const devido = Math.max(0, valorOrig + juros + multa - desc);
  const pago = parseFloat(baixaData.valor_pago) || 0;
  const residuo = Math.max(0, devido - pago);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isReceber ? "Confirmar Recebimento" : "Confirmar Pagamento"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500">Descrição</p>
            <p className="font-medium text-sm line-clamp-2">{item?.descricao}</p>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              <div>
                <p className="text-xs text-slate-500">Valor Original</p>
                <p className="font-bold">{formatCurrency(valorOrig)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Devido</p>
                <p className={`font-bold ${devido !== valorOrig ? 'text-amber-700' : ''}`}>{formatCurrency(devido)}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Data do {isReceber ? "Recebimento" : "Pagamento"}</Label>
              <Input
                type="date"
                value={baixaData.data_pagamento}
                onChange={(e) => setBaixaData({ ...baixaData, data_pagamento: e.target.value })}
                data-testid="input-baixa-data"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Valor {isReceber ? "Recebido" : "Pago"}</Label>
              <Input
                type="number"
                step="0.01"
                value={baixaData.valor_pago}
                onChange={(e) => setBaixaData({ ...baixaData, valor_pago: e.target.value })}
                data-testid="input-baixa-valor-pago"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-red-600">Juros (+)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={baixaData.juros}
                onChange={(e) => setBaixaData({ ...baixaData, juros: e.target.value })}
                data-testid="input-baixa-juros"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-red-600">Multa (+)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={baixaData.multa}
                onChange={(e) => setBaixaData({ ...baixaData, multa: e.target.value })}
                data-testid="input-baixa-multa"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-emerald-700">Desconto (-)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={baixaData.desconto}
                onChange={(e) => setBaixaData({ ...baixaData, desconto: e.target.value })}
                data-testid="input-baixa-desconto"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1 font-semibold text-luvcorp-navy">
              Banco {isReceber ? "Recebedor" : "Pagador"} *
            </Label>
            <NativeSelect
              value={baixaData.banco_id}
              onValueChange={(v) => setBaixaData({ ...baixaData, banco_id: v })}
              placeholder="Selecione o banco…"
              options={[
                { value: "", label: "— Selecione a conta bancária —" },
                ...bancos.map(b => ({ value: b.id, label: b.agencia || b.conta ? `${b.nome} (Ag ${b.agencia || "-"} / CC ${b.conta || "-"})` : b.nome }))
              ]}
              data-testid="select-baixa-banco"
            />
            {!baixaData.banco_id && (
              <p className="text-[10px] text-red-600">Obrigatório: informe de qual conta {isReceber ? "entrou" : "saiu"} o dinheiro.</p>
            )}
            {bancos.length === 0 && (
              <p className="text-[10px] text-amber-600">Nenhum banco cadastrado. Cadastre em Financeiro &gt; Bancos.</p>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Observação da baixa</Label>
            <Input
              placeholder="Opcional (ex: boleto pago no caixa, PIX, etc)"
              value={baixaData.observacao_baixa}
              onChange={(e) => setBaixaData({ ...baixaData, observacao_baixa: e.target.value })}
            />
          </div>

          {residuo > 0.005 && (
            <div className="p-3 rounded border border-amber-300 bg-amber-50 text-amber-900 text-sm" data-testid="aviso-residuo">
              <p className="font-semibold">Baixa parcial detectada</p>
              <p className="text-xs mt-1">
                Será gerado um novo lançamento <b>pendente</b> de <b>{formatCurrency(residuo)}</b> como resíduo,
                preservando todos os vínculos (contrato, fornecedor, categoria).
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={onConfirm}
            disabled={!baixaData.banco_id}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="btn-confirmar-baixa"
          >
            <Check className="h-4 w-4 mr-2" />
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TransferenciaDialog({ open, onOpenChange, transferData, setTransferData, bancos, transferring, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialog-transferencia">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-luvcorp-navy">
            <ArrowLeftRight className="h-5 w-5" />
            Transferência entre Contas Próprias
          </DialogTitle>
          <DialogDescription className="text-xs pt-1">
            Baixa nas duas contas na mesma data. <b>Não gera receita nem despesa</b> — fica fora do DRE.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1">
            <Label className="text-xs">Conta de Origem (saída) *</Label>
            <NativeSelect
              value={transferData.banco_origem_id}
              onValueChange={(v) => setTransferData({ ...transferData, banco_origem_id: v })}
              placeholder="Selecione…"
              options={bancos.map(b => ({ value: b.id, label: b.agencia || b.conta ? `${b.nome} (Ag ${b.agencia || "-"} / CC ${b.conta || "-"})` : b.nome }))}
              data-testid="select-transf-origem"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Conta de Destino (entrada) *</Label>
            <NativeSelect
              value={transferData.banco_destino_id}
              onValueChange={(v) => setTransferData({ ...transferData, banco_destino_id: v })}
              placeholder="Selecione…"
              options={bancos.filter(b => b.id !== transferData.banco_origem_id).map(b => ({ value: b.id, label: b.agencia || b.conta ? `${b.nome} (Ag ${b.agencia || "-"} / CC ${b.conta || "-"})` : b.nome }))}
              data-testid="select-transf-destino"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Valor (R$) *</Label>
              <Input
                type="number" step="0.01" min="0.01"
                value={transferData.valor}
                onChange={(e) => setTransferData({ ...transferData, valor: e.target.value })}
                data-testid="input-transf-valor"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data *</Label>
              <Input
                type="date"
                value={transferData.data}
                onChange={(e) => setTransferData({ ...transferData, data: e.target.value })}
                data-testid="input-transf-data"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Observação</Label>
            <Input
              placeholder="Opcional"
              value={transferData.observacao}
              onChange={(e) => setTransferData({ ...transferData, observacao: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={transferring}>Cancelar</Button>
          <Button
            onClick={onConfirm}
            disabled={transferring || !transferData.banco_origem_id || !transferData.banco_destino_id || !transferData.valor}
            className="bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white"
            data-testid="btn-confirmar-transferencia"
          >
            <ArrowLeftRight className="h-4 w-4 mr-2" />
            {transferring ? "Registrando…" : "Transferir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PdfPreviewDialog({ pdfModal, onClose, isReceber, titulo }) {
  const tituloFinal = titulo || `Relatório • ${isReceber ? "Contas a Receber" : "Contas a Pagar"}`;
  return (
    <Dialog open={pdfModal.open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-6xl w-[95vw] h-[90vh] p-0 overflow-hidden flex flex-col"
        data-testid="dialog-pdf-relatorio"
      >
        <DialogHeader className="px-5 py-3 border-b shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle className="text-base text-luvcorp-navy">
                {tituloFinal}
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">{pdfModal.filename || ""}</p>
            </div>
            <div className="flex gap-2">
              {pdfModal.blobUrl && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(pdfModal.blobUrl, "_blank", "noopener,noreferrer")}
                    data-testid="btn-pdf-abrir-nova-aba"
                  >
                    <Eye className="h-4 w-4 mr-1" /> Abrir em nova aba
                  </Button>
                  <a
                    href={pdfModal.blobUrl}
                    download={pdfModal.filename || "relatorio.pdf"}
                    className="inline-flex"
                    data-testid="btn-pdf-baixar"
                  >
                    <Button size="sm" className="bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white">
                      <Download className="h-4 w-4 mr-1" /> Baixar PDF
                    </Button>
                  </a>
                </>
              )}
            </div>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-hidden bg-slate-100">
          {pdfModal.blobUrl ? (
            <iframe
              src={pdfModal.blobUrl}
              title="Relatório PDF"
              className="w-full h-full border-0"
              data-testid="iframe-pdf-relatorio"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-slate-500 text-sm">
              Aguarde…
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
